import { Dispatch, SetStateAction } from 'react';

import TranslateLangsMap from '@/utils/translate-langs-map.json';
import GetGreenroomUserToken from '@/connection/greenroom/get-greenroom-user-token';
import InitializeGreenroomProfileMask from '@/connection/greenroom/initialize-greenroom-profile-mask';
import SendGreenroomInvite from '@/connection/greenroom/send-greenroom-invite';
import { getAdmins } from '@/store/slices/channel-admins';
import { Channel } from '@/types/channel.types';
import { Meeting } from '@/types/greenroom/meeting.types';
import {
  GreenroomInviteUser,
  GreenroomInviteUserResponse,
  GreenroomUserProfile,
} from '@/types/greenroom/profile.types';
import { CreateSpeaker, ESpeakerInviteStatus, Speaker } from '@/types/speakers.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { useAppDispatch } from '@/store/hooks';
import { isObject } from 'underscore';
import { BLAdmin } from '@/types/profile.types';
import syncGreenroomInvites from '@/micros/edit-project/grid-areas/main/cast-and-crew/components/cast-content/helpers/sync-greenroom-invites';
import GreenroomButton from '@ui/greenroom-button/greenroom-button';
import Text from '@ui/text/text/text';

type Props = {
  workingSpeaker: Speaker | CreateSpeaker;
  setWorkingSpeaker: Dispatch<SetStateAction<Speaker | CreateSpeaker>>;
  userChannel: Channel | null;
  meeting: Meeting | null;
  setInvitingCast: (inviting: boolean) => void;
  setGreenroomProfile: Dispatch<SetStateAction<GreenroomUserProfile | undefined>>;
  dispatch: ReturnType<typeof useAppDispatch>;
  setOpenInviteModal: Dispatch<SetStateAction<boolean>>;
  sessionUuid?: string;
};

const handleSendGreenroomInvite = async (
  props: Props
): Promise<{ inviteResponse: GreenroomInviteUserResponse | null; admins: BLAdmin[] | null }> => {
  const {
    setInvitingCast,
    setOpenInviteModal,
    dispatch,
    setWorkingSpeaker,
    setGreenroomProfile,
    userChannel,
    workingSpeaker,
    meeting,
    sessionUuid,
  } = props;
  if (!userChannel?.channel) {
    return { admins: null, inviteResponse: null };
  }

  if (!workingSpeaker.email) {
    toastTrigger(
      <div className="S1">{!workingSpeaker.email ? <div>Email is required to send an invite.</div> : null}</div>,
      {
        type: 'error',
        autoClose: 10000,
      }
    );
    return { admins: null, inviteResponse: null };
  }
  if (!meeting?.meeting) {
    toastTrigger(
      <div className="S1">
        {!meeting?.meeting ? (
          <Text className="S3">
            <Text>Meeting information not found.</Text>
            <Text>
              You must enter{' '}
              <GreenroomButton
                tooltipProps={{ tooltipWrapperClass: 'display-inline-block' }}
                className="S3 padding-v-0 padding-h-4 radius-4"
                sessionUuid={sessionUuid}
              />{' '}
              to auto generate the necessary meeting data. After entering Greenroom® refresh the page and try again.
            </Text>
          </Text>
        ) : null}
      </div>,
      {
        type: 'warning',
        autoClose: false,
        toastId: 'missing-meeting-data',
      }
    );
    return { admins: null, inviteResponse: null };
  }

  setInvitingCast(true);

  try {
    const greenroomToken = await GetGreenroomUserToken();

    const sendGreenroomInvitePostBody = createSendGreenroomInvitePostBody(props);

    if (!sendGreenroomInvitePostBody) throw new Error('Invalid user details');
    // create the invite and send it
    const sendInviteResponse = await SendGreenroomInvite(sendGreenroomInvitePostBody, greenroomToken?.token);

    // if the invite was sent, update the workingSpeaker
    setWorkingSpeaker((prev) => ({
      ...prev,
      invite_status: ESpeakerInviteStatus.pending,
    }));

    setGreenroomProfile(sendInviteResponse[0]?.profile);

    // initialize the profile mask
    await InitializeGreenroomProfileMask(meeting?.meeting, workingSpeaker.email);

    // refetch channel admins to retrieve latest since GR could've created a new admin if one doesn't exist for the email.
    const adminsResult = await dispatch(
      getAdmins({ channel: userChannel.channel, includeLockedEmails: true, bypassIfExists: false })
    );
    const admins = (isObject(adminsResult.payload) ? adminsResult.payload?.admins || [] : []) as BLAdmin[];

    // if we're successful, send a socket message to GR to update GR redux state to re-render with latest invites
    const meetingId = meeting?.meeting;
    if (sendInviteResponse?.[0] && meetingId) {
      // no need to await since it's just a socket update to GR
      syncGreenroomInvites({ meetingId });
    }

    return { inviteResponse: sendInviteResponse?.[0] || null, admins };
  } catch (e) {
    console.error(e);
    toastTrigger(<div className="S1">There was a problem inviting the cast member</div>, {
      type: 'error',
      autoClose: 5000,
    });
    return { admins: null, inviteResponse: null };
  } finally {
    setInvitingCast(false);
    setOpenInviteModal(false);
  }
};

export default handleSendGreenroomInvite;

const createSendGreenroomInvitePostBody = ({
  userChannel,
  workingSpeaker,
  meeting,
}: Props): GreenroomInviteUser | undefined => {
  if (!userChannel || !workingSpeaker.email) return;

  const greenroomLanguage = (TranslateLangsMap as Record<string, string>)[meeting?.language || 'en'];

  const invitation = {
    email: workingSpeaker.email,
    authentication: 'email_password',
    watcher_only: false,
  };

  return {
    channel: userChannel.channel,
    channel_name: userChannel.name,
    meeting: meeting?.meeting,
    title: `${meeting?.title} - ${greenroomLanguage}`,
    invitations: [invitation],
  };
};
